import React, { useCallback, useEffect } from 'react';
import { path, pick } from 'ramda';
import { useFocus } from '@core/js/hooks/useFocus';
import { useTranslations } from '@core/js/hooks/i18n';
import { Icon } from '@core/js/components/common/Icon';
import { useDispatch } from 'react-redux';
import { useMyAccountSidebarSelector, useUserSelector } from '@core/js/store/hooks';
import { BackboneReplacer } from '@core/js/backbone/components/BackboneReplacer';

import * as hooks from '@core/js/components/main/MyAccountSidebar/hooks';
import * as actions from '@core/js/components/main/MyAccountSidebar/store/actions';

import style from './Header.scss';

const userIcon = (
    <svg width="30" height="30" viewBox="0 0 33 33" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.5625 1C8.03598 1 1.125 7.91098 1.125 16.4375C1.125 24.964 8.03598 31.875 16.5625 31.875C25.089 31.875 32 24.964 32 16.4375C32 7.91098 25.089 1 16.5625 1ZM16.5625 2.28616C24.3651 2.28616 30.7138 8.63369 30.7138 16.4375C30.7138 19.8701 29.4844 23.0215 27.443 25.4744C26.0325 24.8856 22.7013 23.7297 20.6406 23.1204C20.4644 23.0649 20.4367 23.0565 20.4367 22.3241C20.4367 21.7197 20.6852 21.1116 20.9277 20.5964C21.1907 20.0366 21.502 19.0955 21.6142 18.2509C21.9267 17.8877 22.3526 17.1711 22.6265 15.8065C22.8666 14.6036 22.7544 14.1656 22.5951 13.7554C22.5782 13.712 22.5613 13.6697 22.5481 13.6263C22.4877 13.344 22.571 11.8792 22.7761 10.7415C22.9185 9.96087 22.7399 8.3019 21.6649 6.93008C20.9868 6.06258 19.6886 4.99843 17.3178 4.85002L16.0172 4.85123C13.6861 4.99843 12.3879 6.06258 11.7099 6.93008C10.636 8.3019 10.4575 9.96208 10.5998 10.7415C10.8074 11.8792 10.8882 13.344 10.8303 13.6215C10.8182 13.6697 10.8001 13.712 10.782 13.7554C10.624 14.1668 10.5106 14.6036 10.7519 15.8065C11.0245 17.1711 11.4504 17.8877 11.7641 18.2509C11.8751 19.0955 12.1864 20.0366 12.4507 20.5964C12.6425 21.0054 12.733 21.5628 12.733 22.3507C12.733 23.083 12.7052 23.0915 12.5411 23.1434C10.4092 23.7732 7.01647 24.999 5.75082 25.5528C3.66956 23.0891 2.41116 19.9075 2.41116 16.4375C2.41116 8.6349 8.75869 2.28616 16.5625 2.28616ZM6.68226 26.5554C8.1313 25.9642 11.0185 24.9339 12.9164 24.374C14.0191 24.0265 14.0191 23.0975 14.0191 22.3507C14.0191 21.7317 13.9769 20.8196 13.615 20.0486C13.3664 19.5202 13.0817 18.6141 13.0189 17.9058C13.0057 17.7405 12.9272 17.5861 12.8018 17.4775C12.6196 17.3183 12.2492 16.7343 12.0127 15.5555C11.8257 14.6229 11.9053 14.419 11.9813 14.2211C12.0139 14.1367 12.0453 14.0534 12.0706 13.9605C12.225 13.3958 12.0525 11.5414 11.8655 10.5123C11.7835 10.0646 11.8872 8.79416 12.7233 7.72397C13.4738 6.76478 14.6091 6.23029 16.0582 6.13739L17.278 6.13618C18.7656 6.23029 19.901 6.76478 20.6526 7.72397C21.49 8.79416 21.5913 10.0646 21.5093 10.5135C21.3235 11.5414 21.1497 13.3971 21.3041 13.9605C21.3307 14.0546 21.3609 14.1379 21.3934 14.2223C21.4706 14.419 21.5491 14.6241 21.3633 15.5567C21.1268 16.7355 20.7552 17.3195 20.573 17.4787C20.4487 17.5885 20.3703 17.7418 20.3558 17.907C20.2943 18.6165 20.0108 19.5214 19.7622 20.0498C19.4775 20.6555 19.1493 21.4627 19.1493 22.3253C19.1493 23.0722 19.1493 24.0012 20.2641 24.3523C22.0799 24.8892 24.9816 25.8858 26.5139 26.4879C23.9549 29.0216 20.4391 30.59 16.5625 30.59C12.7221 30.59 9.23527 29.0493 6.68347 26.5578L6.68226 26.5554Z" />
    </svg>
);

export const Header = () => {
    const dispatch = useDispatch();
    const translate = useTranslations();
    const [focusRef, setFocus] = useFocus();
    const handleSidebarClose = hooks.useHandleSidebarClose();
    const userName = useUserSelector(path(['userDetails', 'username']));
    const {
        isOpened,
        portalPageURL,
        isBackArrowHidden,
        sidebarMenu: { isSubMenuShown }
    } = useMyAccountSidebarSelector(pick(['isOpened', 'portalPageURL', 'isBackArrowHidden', 'sidebarMenu']));

    const unreadCounterWrapper = (
        <div className={`${style.unreadCounterWrapper} ${style.iconWrapper}`}>
            <BackboneReplacer dynamicName={'UNREAD_MESSAGES_COUNT'} viewName={'unreadmessagescount'} />
        </div>
    );

    const handleBackArrowClick = useCallback(() => {
        portalPageURL ? dispatch(actions.showHomePage()) : dispatch(actions.showMainMenu());
    }, [dispatch, portalPageURL]);

    useEffect(() => {
        if (isOpened) {
            setTimeout(() => {
                setFocus();
            }, 0);
        }
    }, [isOpened, setFocus]);

    return (
        <div className={style.header}>
            <span ref={focusRef} tabIndex={-1} aria-hidden={true} />
            {(portalPageURL && !isBackArrowHidden || !portalPageURL && isSubMenuShown) && (
                <div className={style.backIconContainer}>
                    <Icon
                        role="button"
                        type={'icon'}
                        className={style.backIcon}
                        onClick={handleBackArrowClick}
                        aria-label={translate('playtech.mobile.system.myAccountSidebar.header.aria.backArrow')}
                    />
                </div>
            )}
            <div className={style.headerContent}>
                <div className={style.userIcon}>
                    {userIcon}
                    {unreadCounterWrapper}
                </div>
                <p className={style.title}>
                    {userName}
                </p>
            </div>
            <div className={style.closeIcon} onClick={handleSidebarClose} />
        </div>
    );
};
